import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { MantineProvider } from "@mantine/core";
import { NotificationsProvider } from "@mantine/notifications";

import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";

import axios from "axios";

import store from "./redux/store";
import RouteHandler from "./pages/RouteHandler";

import { firebaseInit } from "./utilities/firebase";
import { loadConfig } from "./configLoader";
import constants from "./utilities/constants";
import { getAppEnv, getEnv, getNodeEnv, isNodeProduction } from "./utilities/env";

import { registerLicense } from "@syncfusion/ej2-base";

import "./index.css";
import "./tailwind.output.css";

/* Syncfusion Styles */
import "@syncfusion/ej2-base/styles/material.css";
import "@syncfusion/ej2-buttons/styles/material.css";
import "@syncfusion/ej2-inputs/styles/material.css";
import "@syncfusion/ej2-popups/styles/material.css";
import "@syncfusion/ej2-lists/styles/material.css";
import "@syncfusion/ej2-navigations/styles/material.css";
import "@syncfusion/ej2-splitbuttons/styles/material.css";
import "@syncfusion/ej2-dropdowns/styles/material.css";
import "@syncfusion/ej2-react-documenteditor/styles/material.css";
import "./components/documentEditor.css";

const setupAxios = () => {
  axios.interceptors.request.use((config) => {
    if (!config.noTrailingSlash && config.url) {
      config.url = config.url.replace(/\/?(\?|#|$)/, "/$1");
    }

    return config;
  });
};

const setupSentry = () => {
  const dsn = getEnv("REACT_APP_SENTRY_DSN");
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: getAppEnv() || getNodeEnv(),
    integrations: [new BrowserTracing()],
    tracesSampleRate: isNodeProduction() ? 0.1 : 1.0,
  });
};

const initializeApp = async () => {
  await loadConfig();

  registerLicense(constants.licenseKey);

  setupAxios();

  setupSentry();

  firebaseInit();

  const container = document.getElementById("root");

  if (!container) {
    throw new Error("Root container not found");
  }

  const root = createRoot(container);

  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <MantineProvider
          theme={{
            fontFamily: "Lexend",
          }}
        >
          <NotificationsProvider autoClose={5000}>
            <BrowserRouter>
              <RouteHandler />
            </BrowserRouter>
          </NotificationsProvider>
        </MantineProvider>
      </Provider>
    </React.StrictMode>,
  );
};

initializeApp().catch((error) => {
  console.error("Application initialization failed:", error.message);
});
